import React, { useCallback, useEffect, useState } from 'react'
import { ISocialShareProps, IWindowInner, IShareTitleUrl } from './types'
import {
  EmailShareButton,
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'next-share'
import Icon from '@/components/base/icon'
import { useApp } from '@wap-client/core'
import useTranslations from '@/hooks/useTranslations'

const SocialShare: React.FC<ISocialShareProps> = ({
  iconSize = 'medium',
  gap = 8,
  url,
  title,
  desc,
  marginT,
  marginB,
}) => {
  const [windowSize, setWindowSize] = useState<IWindowInner>({
    windowWidth: 0,
    windowHeight: 0,
  })
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [shareTitleUrl, setShareTitleUrl] = useState<IShareTitleUrl>({
    title: '',
    url: '',
  })

  const app = useApp()
  const { translation } = useTranslations()

  const getShareTitleUrl = useCallback(() => {
    let shareTitle = title ?? app.page.title
    let shareUrl = url ?? window.location.href

    if (!shareUrl.startsWith('http://') && !shareUrl.startsWith('https://')) {
      shareUrl = `https://${shareUrl}`
    }

    if (
      shareTitle.localeCompare(app.settings.homepage, undefined, {
        sensitivity: 'accent',
      }) === 0
    ) {
      shareTitle = translation('project-title')
    }

    setShareTitleUrl({ title: shareTitle, url: shareUrl })
  }, [app.page.title, app.settings.homepage, translation, title, url])

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(() => {
        const windowW =
          window?.innerWidth > 1366 ? 1200 : (window?.innerWidth * 80) / 100
        const windowH =
          window?.innerHeight > 1100 ? 1024 : (window?.innerHeight * 80) / 100

        return { windowWidth: windowW, windowHeight: windowH }
      })

      if (window.innerWidth <= 768 && !isMobile) {
        setIsMobile(true)
      } else if (window.innerWidth > 768 && isMobile) {
        setIsMobile(false)
      }
    }

    handleResize()
    getShareTitleUrl()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isMobile, getShareTitleUrl])

  const renderWebSocialShare = () => {
    return (
      <div
        className="social-share-web-content"
        style={{ columnGap: `${gap}px` }}
      >
        <FacebookShareButton
          title={shareTitleUrl.title}
          url={shareTitleUrl.url}
          {...windowSize}
        >
          <Icon
            name="facebook"
            size={iconSize}
            title={translation('share-on-facebook')}
          />
        </FacebookShareButton>
        <TwitterShareButton
          title={shareTitleUrl.title}
          url={shareTitleUrl.url}
          {...windowSize}
        >
          <Icon
            name="twitter-x"
            size={iconSize}
            title={translation('share-on-twitter')}
          />
          ,
        </TwitterShareButton>
        <WhatsappShareButton
          title={shareTitleUrl.title}
          url={shareTitleUrl.url}
          {...windowSize}
        >
          <Icon
            name="whatsapp"
            size={iconSize}
            title={translation('share-on-whatsapp')}
          />
        </WhatsappShareButton>
        <TelegramShareButton
          title={shareTitleUrl.title}
          url={shareTitleUrl.url}
          {...windowSize}
        >
          <Icon
            name="telegram"
            size={iconSize}
            title={translation('share-on-telegram')}
          />
        </TelegramShareButton>
        <EmailShareButton
          url={shareTitleUrl.url}
          subject={translation('project-title')}
          blankTarget
          body={shareTitleUrl.title}
        >
          <Icon
            name="email-filled"
            size={iconSize}
            title={translation('share-on-email')}
          />
        </EmailShareButton>
      </div>
    )
  }

  const renderMobileSocialShare = () => {
    const hanlePageShareMobile = () => {
      if (navigator.share) {
        navigator
          .share({
            title: shareTitleUrl.title,
            text: desc ?? '',
            url: shareTitleUrl.url,
          })
          .then(() => {})
          .catch((error) => console.log('Error sharing', error))
      }
    }

    return (
      <div className="social-share-mobile-content">
        <button
          className="social-share-mobile-content-share-button"
          type="button"
          onClick={hanlePageShareMobile}
        >
          <Icon name="share" size="medium" />
          <span>{translation('share')}</span>
        </button>
      </div>
    )
  }

  return (
    <div
      className="social-share"
      style={{ marginTop: marginT, marginBottom: marginB }}
    >
      {!isMobile ? renderWebSocialShare() : renderMobileSocialShare()}
    </div>
  )
}

export default SocialShare
